<template>
    <div class="member-history-container">
        <div class="history-header">
            <h2>{{ title }}</h2>
        </div>

        <div class="filter-row" v-if="showCategoryFilter && categoryOptions.length > 0">
            <label>Filter by category:</label>
            <md-field class="category-filter">
                <md-select v-model="selectedCategory" @md-selected="handleFilterCategory">
                    <md-option value="all">All</md-option>
                    <md-option v-for="option in categoryOptions" :key="option.id" :value="option.id">
                        {{ option.name }}
                    </md-option>
                </md-select>
            </md-field>
            <span class="entry-count">
                Showing {{ filteredItems.length }} of {{ hasMore ? totalCount : filteredItems.length }} entries
            </span>
        </div>

        <div v-if="loading" class="loading-container">
            <p>Loading history...</p>
        </div>
        <div v-else-if="filteredItems.length === 0" class="empty-state">
            <p>{{ emptyStateMessage }}</p>
        </div>
        <div v-else class="history-table" ref="historyTable">
            <table>
                <thead>
                    <tr>
                        <th>TIMESTAMP</th>
                        <th>USER</th>
                        <th>CATEGORY</th>
                        <th>CHANGE</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(item, index) in filteredItems" :key="getUniqueKey(item, index)">
                        <td class="timestamp">{{ item.createdDate | dateFormat(DATE_TYPES.standardDateTime) }}</td>
                        <td class="user">{{ item.changedByUserFullName }}</td>
                        <td class="category">
                            <span class="badge" :class="getCategoryBadgeClass(item.category)">
                                {{ getCategoryName(item.category) }}
                            </span>
                        </td>
                        <td class="change">
                            <span class="field-name">{{ item.fieldName }}</span>
                            <!-- JSON diff view when both values are JSON -->
                            <span v-if="shouldShowJsonDiff(item.oldValue, item.newValue)">
                                <div
                                    v-for="(change, changeIndex) in getJsonDiff(item.oldValue, item.newValue)"
                                    :key="`${getUniqueKey(item, index)}-change-${changeIndex}`"
                                    class="json-change"
                                >
                                    <span class="json-path">{{ change.path }}</span>
                                    <span class="change-text">changed from</span>
                                    <span v-if="isBlank(change.oldValue)" class="blank-value">blank</span>
                                    <span v-else class="old-value">{{ change.oldValue }}</span>
                                    <span class="change-text">to</span>
                                    <span v-if="isBlank(change.newValue)" class="blank-value">blank</span>
                                    <span v-else class="new-value">{{ change.newValue }}</span>
                                </div>
                            </span>
                            <!-- JSON set view when old is blank and new is JSON -->
                            <span v-else-if="isBlank(item.oldValue) && $_isJson(item.newValue)">
                                <span class="change-text">set to</span>
                                <div class="json-set-view">
                                    <div
                                        v-for="(prop, propIndex) in getJsonProperties(item.newValue)"
                                        :key="`${getUniqueKey(item, index)}-prop-${propIndex}`"
                                        class="json-property"
                                    >
                                        <span v-if="prop.path" class="json-path">{{ prop.path }}:</span>
                                        <span class="new-value">{{ prop.value }}</span>
                                    </div>
                                </div>
                            </span>
                            <!-- JSON cleared view when new is blank and old is JSON -->
                            <span v-else-if="$_isJson(item.oldValue) && isBlank(item.newValue)">
                                <span class="change-text">cleared (was:</span>
                                <div class="json-set-view">
                                    <div
                                        v-for="(prop, propIndex) in getJsonProperties(item.oldValue)"
                                        :key="`${getUniqueKey(item, index)}-prop-${propIndex}`"
                                        class="json-property"
                                    >
                                        <span v-if="prop.path" class="json-path">{{ prop.path }}:</span>
                                        <span class="old-value">{{ prop.value }}</span>
                                    </div>
                                </div>
                                <span class="change-text">)</span>
                            </span>
                            <!-- Standard view for non-JSON values -->
                            <span v-else>
                                <!-- Insert: "added [newValue]" -->
                                <span v-if="item.changeType === 'Insert'">
                                    <span class="change-text">added</span>
                                    <span class="new-value">{{ item.newValue }}</span>
                                </span>
                                <!-- Delete: "removed [oldValue]" -->
                                <span v-else-if="item.changeType === 'Delete'">
                                    <span class="change-text">removed</span>
                                    <span class="old-value">{{ item.oldValue }}</span>
                                </span>
                                <!-- Update: "changed from [oldValue] to [newValue]" -->
                                <span v-else>
                                    <span class="change-text">changed from</span>
                                    <span :class="getValueClass(item.oldValue, 'old')">
                                        {{ item.oldValue || 'blank' }}
                                    </span>
                                    <span class="change-text">to</span>
                                    <span :class="getValueClass(item.newValue, 'new')">
                                        {{ item.newValue || 'blank' }}
                                    </span>
                                </span>
                            </span>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div class="load-more" v-if="hasMore && !loading">
            <button class="custom-btn" @click="handleLoadMore" :disabled="loadingMore">
                {{ loadingMore ? 'Loading...' : 'Load More' }}
            </button>
        </div>
    </div>
</template>

<script>
import { handleRequests, showErrorMessage } from '@/helpers';
import { DATE_TYPES, CHANGE_HISTORY_VIEW_TYPES } from '@/utils/constants';
import { FormattingMixin } from '@/mixins';

export default {
    name: 'HistoryComponent',
    mixins: [FormattingMixin],
    props: {
        /**
         * The view type to use for fetching change history.
         * Maps to ChangeHistoryViewType enum in the backend.
         * Use CHANGE_HISTORY_VIEW_TYPES constants from @/utils/constants.
         */
        viewType: {
            type: Number,
            required: true,
            validator: (value) => Object.values(CHANGE_HISTORY_VIEW_TYPES).includes(value)
        },
        /**
         * The ID of the entity to get change history for.
         */
        entityId: {
            type: Number,
            required: true
        },
        /**
         * Whether to show the category filter dropdown.
         */
        showCategoryFilter: {
            type: Boolean,
            default: true
        },
        /**
         * Number of items to fetch per page.
         */
        pageSize: {
            type: Number,
            default: 15
        },
        /**
         * Title displayed in the component header.
         */
        title: {
            type: String,
            default: 'History'
        },
        /**
         * Message displayed when there are no history items.
         */
        emptyStateMessage: {
            type: String,
            default: 'No history available.'
        }
    },
    data() {
        return {
            historyItems: [],
            categoryOptions: [],
            totalCount: 0,
            hasMore: false,
            loading: false,
            loadingMore: false,
            selectedCategory: 'all',
            pageNumber: 1,
            DATE_TYPES
        };
    },
    computed: {
        /**
         * Items filtered by selected category (client-side filtering).
         */
        filteredItems() {
            if (this.selectedCategory === 'all') {
                return this.historyItems;
            }
            return this.historyItems.filter((item) => item.category === this.selectedCategory);
        }
    },
    watch: {
        entityId: {
            handler(newVal, oldVal) {
                if (newVal !== oldVal && newVal > 0) {
                    this.resetAndLoad();
                }
            },
            immediate: true
        },
        viewType: {
            handler(newVal, oldVal) {
                if (newVal !== oldVal) {
                    this.resetAndLoad();
                }
            }
        }
    },
    methods: {
        /**
         * Public method to refresh the history data.
         * Can be called from parent component via ref.
         * Example: this.$refs.historyComponent.refresh()
         */
        refresh() {
            this.resetAndLoad();
        },

        resetAndLoad() {
            this.selectedCategory = 'all';
            this.pageNumber = 1;
            this.categoryOptions = [];
            this.loadHistory();
        },

        handleFilterCategory() {
            // Client-side filtering, no need to reload
        },

        handleLoadMore() {
            if (this.loadingMore || !this.hasMore) {
                return;
            }

            this.loadingMore = true;
            this.pageNumber += 1;
            this.loadHistoryPage(true);
        },

        async loadHistory() {
            this.loading = true;
            this.historyItems = [];
            await this.loadHistoryPage(false);
            this.loading = false;
        },

        async loadHistoryPage(append = false) {
            try {
                const endpoint = this.buildEndpoint();
                const response = await handleRequests(endpoint, { method: 'get' });

                if (response?.data) {
                    const { data, totalCount, hasMore, categories } = response.data;

                    // Update category options from first page response
                    if (categories && categories.length > 0) {
                        this.categoryOptions = categories;
                    }

                    if (append) {
                        // Deduplicate items
                        const existingKeys = new Set(this.historyItems.map((item) => this.generateItemKey(item)));
                        const newItems = data.filter((item) => !existingKeys.has(this.generateItemKey(item)));
                        this.historyItems = [...this.historyItems, ...newItems];
                    } else {
                        this.historyItems = data || [];
                    }

                    this.totalCount = totalCount;
                    this.hasMore = hasMore;

                    if (append) {
                        this.$nextTick(() => {
                            this.scrollToBottom();
                        });
                    }
                }
            } catch (error) {
                showErrorMessage(this, 'Error loading history', error);
            } finally {
                this.loadingMore = false;
            }
        },

        buildEndpoint() {
            const params = new URLSearchParams();
            params.set('pageNumber', this.pageNumber);
            params.set('pageSize', this.pageSize);

            return `/api/change-history/${this.viewType}/${this.entityId}?${params.toString()}`;
        },

        scrollToBottom() {
            const container = this.$refs.historyTable;
            if (container) {
                container.scrollTo({
                    top: container.scrollHeight,
                    behavior: 'smooth'
                });
            }
        },

        getCategoryName(categoryId) {
            const category = this.categoryOptions.find((c) => c.id === categoryId);
            return category ? category.name : 'Unknown';
        },

        getCategoryBadgeClass(categoryId) {
            const categoryName = this.getCategoryName(categoryId);
            const classes = {
                Details: 'badge-details',
                Accounts: 'badge-accounts',
                Rostering: 'badge-rostering',
                Documents: 'badge-documents',
                Trip: 'badge-trip',
                Charges: 'badge-charges',
                Costs: 'badge-costs',
                Services: 'badge-services'
            };
            return classes[categoryName] || 'badge-default';
        },

        getValueClass(value, type) {
            if (!value || value === 'blank') {
                return 'blank-value';
            }
            return type === 'old' ? 'old-value' : 'new-value';
        },

        isBlank(value) {
            if (value === null || value === undefined || value === '') {
                return true;
            }

            if (Array.isArray(value) && value.length === 0) {
                return true;
            }

            if (typeof value === 'string') {
                const trimmed = value.trim();
                if (trimmed === '[]' || trimmed === '""') {
                    return true;
                }
            }

            return false;
        },

        shouldShowJsonDiff(oldValue, newValue) {
            if (this.isBlank(oldValue) || this.isBlank(newValue)) {
                return false;
            }

            return this.$_isJson(oldValue) && this.$_isJson(newValue);
        },

        getJsonDiff(oldValueStr, newValueStr) {
            try {
                const oldObj = JSON.parse(oldValueStr);
                const newObj = JSON.parse(newValueStr);

                const changes = [];
                this.findJsonDifferences(oldObj, newObj, '', changes);

                return changes;
            } catch (e) {
                return [];
            }
        },

        findJsonDifferences(oldObj, newObj, path, changes) {
            if (oldObj === null && newObj === null) {
                return;
            }
            if (oldObj === undefined && newObj === undefined) {
                return;
            }

            // Handle case where one side is null/undefined and the other is an object
            // Recurse into the object to show each property being added/removed
            const oldIsNullish = oldObj === null || oldObj === undefined;
            const newIsNullish = newObj === null || newObj === undefined;

            if (oldIsNullish && !newIsNullish && typeof newObj === 'object') {
                // New object added - show each property as added
                Object.keys(newObj).forEach((key) => {
                    const newPath = path ? `${path} > ${key}` : key;
                    this.findJsonDifferences(undefined, newObj[key], newPath, changes);
                });
                return;
            }

            if (!oldIsNullish && newIsNullish && typeof oldObj === 'object') {
                // Object removed - show each property as removed
                Object.keys(oldObj).forEach((key) => {
                    const newPath = path ? `${path} > ${key}` : key;
                    this.findJsonDifferences(oldObj[key], undefined, newPath, changes);
                });
                return;
            }

            if (typeof oldObj !== typeof newObj) {
                changes.push({
                    path: this.getLeafPropertyName(path),
                    oldValue: this.formatPrimitiveValue(oldObj),
                    newValue: this.formatPrimitiveValue(newObj)
                });
                return;
            }

            if (typeof oldObj !== 'object' || oldObj === null) {
                if (oldObj !== newObj) {
                    changes.push({
                        path: this.getLeafPropertyName(path),
                        oldValue: this.formatPrimitiveValue(oldObj),
                        newValue: this.formatPrimitiveValue(newObj)
                    });
                }
                return;
            }

            const allKeys = new Set([...Object.keys(oldObj || {}), ...Object.keys(newObj || {})]);

            Array.from(allKeys).forEach((key) => {
                const newPath = path ? `${path} > ${key}` : key;
                this.findJsonDifferences(oldObj[key], newObj[key], newPath, changes);
            });
        },

        getLeafPropertyName(path) {
            if (!path) {
                return 'root';
            }

            const parts = path.split(' > ');

            // For nested properties (more than 1 level deep), show parent > leaf
            // e.g., "driverLicense > documentNumber" becomes "Driver License > Document Number"
            if (parts.length >= 2) {
                return parts.map((part) => this.$_toTitleCase(part)).join(' > ');
            }

            return this.$_toTitleCase(parts[0]);
        },

        getJsonProperties(jsonStr) {
            try {
                const obj = JSON.parse(jsonStr);
                const properties = [];
                this.extractJsonProperties(obj, '', properties);
                return properties;
            } catch (e) {
                return [];
            }
        },

        extractJsonProperties(obj, path, properties) {
            if (obj === null || obj === undefined) {
                return;
            }

            // Handle primitive values
            if (typeof obj !== 'object') {
                properties.push({
                    path: this.getLeafPropertyName(path) || 'Value',
                    value: this.formatPrimitiveValue(obj)
                });
                return;
            }

            // Handle arrays - join primitive arrays into comma-separated string
            if (Array.isArray(obj)) {
                // Check if it's an array of primitives
                const allPrimitives = obj.every((item) => typeof item !== 'object' || item === null);
                if (allPrimitives) {
                    const formattedValue = obj.map((item) => this.formatPrimitiveValue(item)).join(', ');
                    // If at root level (no path), just show the values without a label
                    if (!path) {
                        properties.push({
                            path: '',
                            value: formattedValue
                        });
                    } else {
                        properties.push({
                            path: this.getLeafPropertyName(path),
                            value: formattedValue
                        });
                    }
                    return;
                }
                // For arrays of objects, recurse with index
                obj.forEach((item, index) => {
                    const newPath = path ? `${path} > Item ${index + 1}` : `Item ${index + 1}`;
                    this.extractJsonProperties(item, newPath, properties);
                });
                return;
            }

            // Handle objects
            Object.keys(obj).forEach((key) => {
                const newPath = path ? `${path} > ${key}` : key;
                const value = obj[key];

                if (value !== null && typeof value === 'object') {
                    this.extractJsonProperties(value, newPath, properties);
                } else {
                    properties.push({
                        path: this.getLeafPropertyName(newPath),
                        value: this.formatPrimitiveValue(value)
                    });
                }
            });
        },

        formatPrimitiveValue(value) {
            if (value === null || value === undefined) {
                return '';
            }
            if (typeof value === 'boolean') {
                return value ? 'Yes' : 'No';
            }
            if (typeof value === 'string') {
                return value;
            }
            if (typeof value === 'number') {
                return String(value);
            }
            return String(value);
        },

        /**
         * Generates a unique identifier for deduplication.
         */
        generateItemKey(item) {
            const timestamp = new Date(item.createdDate).getTime();
            return `${item.id}-${item.fieldName}-${timestamp}`;
        },

        /**
         * Generates a unique key for Vue's v-for rendering.
         */
        getUniqueKey(item, index) {
            const timestamp = new Date(item.createdDate).getTime();
            return `history-${item.id}-${item.fieldName}-${timestamp}-${index}`;
        }
    }
};
</script>

<style scoped lang="scss">
.member-history-container {
    background: white;
    border-radius: 8px;
    padding: 24px;
}

.history-header {
    margin-bottom: 20px;

    h2 {
        font-size: 20px;
        font-weight: 500;
        margin: 0;
        color: #333;
    }
}

.filter-row {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 20px;

    label {
        font-size: 14px;
        color: #666;
    }

    .category-filter {
        width: 200px;
        margin: 0;
    }

    .entry-count {
        font-size: 13px;
        color: #999;
        margin-left: auto;
    }
}

.loading-container,
.empty-state {
    text-align: center;
    padding: 40px 20px;
    color: #999;
    font-size: 16px;
}

.history-table {
    width: 100%;
    overflow-x: auto;
    max-height: 500px;
    overflow-y: auto;
    position: relative;

    table {
        width: 100%;
        border-collapse: collapse;

        thead {
            position: sticky;
            top: 0;
            z-index: 10;
            background-color: white;
            border-bottom: 1px solid #eee;
            box-shadow: 0 2px 2px -1px rgba(0, 0, 0, 0.05);

            th {
                text-align: left;
                padding: 12px 16px;
                font-size: 11px;
                font-weight: 600;
                color: #666;
                text-transform: uppercase;
                letter-spacing: 0.5px;
                background-color: white;
            }
        }

        tbody {
            tr {
                border-bottom: 1px solid #f5f5f5;

                &:hover {
                    background-color: #fafafa;
                }

                td {
                    padding: 16px;
                    font-size: 14px;
                    vertical-align: middle;

                    &.timestamp {
                        color: #333;
                        white-space: nowrap;
                    }

                    &.user {
                        color: #333;
                        width: 12%;
                    }

                    &.category {
                        color: #333;
                        width: 12%;
                    }

                    &.change {
                        .field-name {
                            font-weight: 500;
                            color: #333;
                        }

                        .change-text {
                            color: #666;
                            margin: 0 6px;
                        }

                        .old-value {
                            color: #d32f2f;
                            font-weight: 500;
                        }

                        .new-value {
                            color: #2e7d32;
                            font-weight: 500;
                        }

                        .blank-value {
                            color: #999;
                            font-style: italic;
                            font-weight: 400;
                        }

                        .json-change {
                            display: block;
                            margin: 4px 0;
                            padding-left: 12px;
                            border-left: 3px solid #e0e0e0;
                        }

                        .json-set-view {
                            display: inline-block;
                            margin-top: 4px;

                            .json-property {
                                display: block;
                                margin: 2px 0;
                                padding-left: 12px;
                                border-left: 3px solid #c8e6c9;
                            }
                        }

                        .json-path {
                            font-weight: 600;
                            color: #1976d2;
                            margin-right: 8px;
                        }
                    }
                }
            }
        }
    }
}

.badge {
    position: initial;
    display: inline-block;
    padding: 4px 12px;
    border-radius: 12px;
    font-size: 12px;
    margin: unset;
    font-weight: 500;
    width: unset;
    max-width: 100%;
    height: unset;
    line-height: 1.5;

    &.badge-details,
    &.badge-trip {
        background-color: #1976d2;
        color: #fff;
    }

    &.badge-accounts,
    &.badge-charges {
        background-color: #2e7d32;
        color: #fff;
    }

    &.badge-rostering,
    &.badge-costs {
        background-color: #f57c00;
        color: #fff;
    }

    &.badge-documents,
    &.badge-services {
        background-color: #7b1fa2;
        color: #fff;
    }

    &.badge-default {
        background-color: #666;
        color: #fff;
    }
}

.load-more {
    text-align: center;
    margin-top: 24px;

    .custom-btn {
        background: #2196f3;
        color: white;
        border: none;
        border-radius: 4px;
        padding: 10px 24px;
        cursor: pointer;
        font-size: 14px;
        font-weight: 500;
        transition: background 0.2s;

        &:hover:not(:disabled) {
            background: #1976d2;
        }

        &:disabled {
            background: #90caf9;
            cursor: not-allowed;
            opacity: 0.7;
        }
    }
}
</style>
